/**
 * The sentiment screen's own catalogue. Figures, units and legends are data
 * and render through `src/chrome/measure`; this holds only the labels and the
 * states a reader has to act on.
 */

import { defineCatalogue } from '../../../i18n';

export const sentimentCatalogue = defineCatalogue(
  {
    title: 'Sentimento',

    'filters.label': 'Filtros do sentimento',
    'scope.kind': 'Escopo',
    'scope.campaign': 'Janela da campanha',
    'scope.account': 'Conta',
    'scope.post': 'Publicação',
    'scope.ref.account': 'Escolher conta',
    'scope.ref.post': 'Escolher publicação',

    empty: 'Ainda não pontuado',
    emptyHint: 'A pontuação semanal ainda não correu para este escopo nesta janela.',

    'level.none': 'Sem nível de influência',
    'level.named': 'Nível de influência: {level}',

    'headline.title': 'Como a campanha está sendo recebida',
    'headline.caption': 'NCS · saldo entre elogio e crítica',
    'headline.fell.one': '↓ caiu {n} ponto em 7 dias',
    'headline.fell.other': '↓ caiu {n} pontos em 7 dias',
    'headline.rose.one': '↑ subiu {n} ponto em 7 dias',
    'headline.rose.other': '↑ subiu {n} pontos em 7 dias',
    'headline.steady': 'estável em 7 dias',
    'headline.axisMin': '−100',
    'headline.axisMax': '+100',
    'headline.range': 'Intervalo da última semana, de −100 a +100',
    'headline.basis': 'Base da leitura',
    'headline.read': 'Comentários analisados',
    'headline.period': 'Período',
    'headline.periodValue': '{from} a {to}',

    'series.methodValue': 'v{version}',
    'series.label': 'Sentimento líquido por semana, com o intervalo',
    'series.versionChange': 'método v{from} → v{to}',
    'series.noReading': 'sem leitura',
    'series.point': '{week}: {score}, {sample}, {interval}',

    'daily.title': 'Dia a dia',
    'daily.label': 'Elogio, neutro e crítica por período',
    'daily.supportive': 'elogio',
    'daily.neutral': 'neutro',
    'daily.hostile': 'crítica',
    'daily.empty': 'Sem comentários neste dia.',
    'daily.withheld': 'Retido: poucos comentários para não identificar ninguém.',
    'daily.withheldNote': 'Dias com poucos comentários ficam ocultos para proteger a privacidade.',
    'daily.readers': 'Quem comenta não representa quem lê.',
    'daily.bar': '{bucket}: {supportive} elogio, {neutral} neutro, {hostile} crítica, saldo {net}',
    'daily.net': 'saldo (elogio − crítica)',
    'daily.legend': 'Legenda',

    'terms.title': 'O que estão dizendo',
    'terms.label': 'Termos mais citados',
    'terms.mentions.one': '{n} menção',
    'terms.mentions.other': '{n} menções',
    'terms.detail':
      '{term} · {mentions} · {supportive} elogio · {neutral} neutro · {hostile} crítica',

    'topics.against': 'Assuntos que mais pesam contra',
    'topics.by': 'Por assunto',
    'topics.withheld': 'Assuntos com poucos comentários ficam ocultos para proteger a privacidade.',
    'topics.more': 'Ver mais ({n})',
    'topics.subthemes.one': '{n} subtema',
    'topics.subthemes.other': '{n} subtemas',
    'topics.others.one': 'Outros: {n} assunto pequeno, {comments}',
    'topics.others.other': 'Outros: {n} assuntos pequenos, {comments}',

    'comments.one': '{n} comentário analisado',
    'comments.other': '{n} comentários analisados',
    'comments.none': 'sem comentário analisado',
    'score.none': 'sem leitura',
    'score.chip': 'NCS {score}',

    'region.title': 'Por região',

    'tags.title': 'Por tag da conta',
    'tags.untagged': 'Nenhuma conta com tags de {name}',
    'tags.silent': 'As contas com tags de {name} não tiveram comentários nesta janela',
    'tags.tagAccounts': 'Marcar contas',

    'posts.title': 'Publicações que mais puxaram para baixo',
    'posts.all': 'abrir a lista',
    'posts.open': 'Abrir a publicação de {name}',
    'posts.unnamed': 'Conta sem nome',
  },
  {
    title: 'Sentiment',

    'filters.label': 'Sentiment filters',
    'scope.kind': 'Scope',
    'scope.campaign': 'Campaign window',
    'scope.account': 'Account',
    'scope.post': 'Post',
    'scope.ref.account': 'Choose account',
    'scope.ref.post': 'Choose post',

    empty: 'Not yet scored',
    emptyHint: 'The weekly scoring has not yet run for this scope in this window.',

    'level.none': 'No influence level',
    'level.named': 'Influence level: {level}',

    'headline.title': 'How the campaign is being received',
    'headline.caption': 'NCS · balance of praise and criticism',
    'headline.fell.one': '↓ fell {n} point in 7 days',
    'headline.fell.other': '↓ fell {n} points in 7 days',
    'headline.rose.one': '↑ rose {n} point in 7 days',
    'headline.rose.other': '↑ rose {n} points in 7 days',
    'headline.steady': 'steady over 7 days',
    'headline.axisMin': '−100',
    'headline.axisMax': '+100',
    'headline.range': 'Last week interval, from −100 to +100',
    'headline.basis': 'Reading basis',
    'headline.read': 'Comments analysed',
    'headline.period': 'Period',
    'headline.periodValue': '{from} to {to}',

    'series.methodValue': 'v{version}',
    'series.label': 'Net sentiment by week, with the interval',
    'series.versionChange': 'method v{from} → v{to}',
    'series.noReading': 'no reading',
    'series.point': '{week}: {score}, {sample}, {interval}',

    'daily.title': 'Day by day',
    'daily.label': 'Praise, neutral and criticism by period',
    'daily.supportive': 'praise',
    'daily.neutral': 'neutral',
    'daily.hostile': 'criticism',
    'daily.empty': 'No comments on this day.',
    'daily.withheld': 'Withheld: too few comments to keep anyone anonymous.',
    'daily.withheldNote': 'Days with few comments are hidden to protect privacy.',
    'daily.readers': 'Commenters do not represent readers.',
    'daily.bar': '{bucket}: {supportive} praise, {neutral} neutral, {hostile} criticism, net {net}',
    'daily.net': 'net (praise − criticism)',
    'daily.legend': 'Legend',

    'terms.title': 'What they are saying',
    'terms.label': 'Most cited terms',
    'terms.mentions.one': '{n} mention',
    'terms.mentions.other': '{n} mentions',
    'terms.detail':
      '{term} · {mentions} · {supportive} praise · {neutral} neutral · {hostile} criticism',

    'topics.against': 'Topics weighing most against',
    'topics.by': 'By topic',
    'topics.withheld': 'Topics with few comments are hidden to protect privacy.',
    'topics.more': 'Show more ({n})',
    'topics.subthemes.one': '{n} sub-theme',
    'topics.subthemes.other': '{n} sub-themes',
    'topics.others.one': 'Others: {n} small topic, {comments}',
    'topics.others.other': 'Others: {n} small topics, {comments}',

    'comments.one': '{n} comment analysed',
    'comments.other': '{n} comments analysed',
    'comments.none': 'no comments analysed',
    'score.none': 'no reading',
    'score.chip': 'NCS {score}',

    'region.title': 'By region',

    'tags.title': 'By account tag',
    'tags.untagged': 'No account carries a {name} tag',
    'tags.silent': 'The accounts tagged {name} had no comments in this window',
    'tags.tagAccounts': 'Tag accounts',

    'posts.title': 'Posts that pulled sentiment down most',
    'posts.all': 'open the list',
    'posts.open': 'Open the post by {name}',
    'posts.unnamed': 'Unnamed account',
  },
);

export const st2 = sentimentCatalogue.t;
export const st2Plural = sentimentCatalogue.plural;
